import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { Button } from "@heroui/react";

import { ICON_SIZE } from "@/config/constants";
import { storage } from "@/shared/utils/local-storage";

interface MobileNotesSearchProps {
  onSearchChange?: (value: string) => void;
}

export function NotesSearchMobile({ onSearchChange }: MobileNotesSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");
  const [isActive, setIsActive] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    const storedSearch = storage.getNotesSearch();
    storage.setNotesSearch({ text: value, tags: storedSearch.tags });
    onSearchChange?.(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      inputRef.current?.blur();
    } else if (event.key === "Enter") {
      inputRef.current?.blur();
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      // Check if focus is still within the search component
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setIsActive(false);
      }
    }, 100);
  };

  const handleSearchButtonClick = () => {
    setIsActive(true);
  };

  const handleButtonKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSearchButtonClick();
    }
  };

  const handleClearAll = () => {
    setSearchValue("");
    const storedSearch = storage.getNotesSearch();
    storage.setNotesSearch({ text: "", tags: storedSearch.tags });
    onSearchChange?.("");
  };

  useEffect(() => {
    const storedSearch = storage.getNotesSearch();
    setSearchValue(storedSearch.text);
    onSearchChange?.(storedSearch.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only on mount

  return (
    <motion.div
      ref={containerRef}
      className={clsx(
        "transition-duration-150 relative flex min-h-8 items-center gap-2 rounded-lg p-2 transition-opacity",
        {
          "cursor-text": isActive,
          "focus:ring-primary cursor-pointer hover:opacity-90 focus:ring-1 focus:ring-offset-1 focus:outline-none":
            !isActive,
        }
      )}
      onClick={!isActive ? handleSearchButtonClick : undefined}
      onKeyDown={!isActive ? handleButtonKeyDown : undefined}
      role={!isActive ? "button" : "group"}
      tabIndex={!isActive ? 0 : -1}
      aria-label={!isActive ? (searchValue ? "Search notes (search active)" : "Search notes") : "Search form"}
      aria-expanded={isActive}
      animate={{
        width: isActive ? "100%" : "min-content",
      }}
      transition={{
        duration: 0.15,
        ease: "easeInOut",
      }}
    >
      <MagnifyingGlassIcon
        className="shrink-0"
        size={ICON_SIZE.md}
        aria-hidden="true"
        weight={!isActive && searchValue ? "fill" : "regular"}
      />

      {isActive && (
        <input
          autoFocus
          ref={inputRef}
          className="placeholder:text-foreground-500 w-full bg-transparent text-sm outline-none focus:outline-none"
          placeholder="Search notes..."
          type="text"
          value={searchValue}
          onChange={handleSearch}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          aria-label="Search input"
          role="searchbox"
        />
      )}

      {isActive && searchValue ? (
        <Button
          className="h-5 min-h-5 w-5 min-w-5 p-0"
          isIconOnly
          radius="full"
          size="sm"
          onPress={handleClearAll}
          aria-label="Clear search"
        >
          <XIcon size={10} weight="bold" aria-hidden="true" />
        </Button>
      ) : null}
    </motion.div>
  );
}
